/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import React, { ReactNode, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// components imports
import { Alert, Button, Col, Popconfirm, Row, Space, Table, Tooltip, message } from "antd";
import { CopyOutlined, DeleteOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";

// misc imports
import { useDeleteApiKey } from "@/api/services";
import { MONTHS_MAP, PAGINATION_OPTIONS } from "@/constants";
import { ApiKeyModal } from "../ApiKeyModal";
import { IApiKey } from "@/interfaces/Service.interface";

export const ApiKeysSettings = ({ serviceKey, title, apiKeys }: { serviceKey: string; title: ReactNode; apiKeys: IApiKey[] }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { workspaceId, projectId } = useParams();
  const [messageApi, contextHolder] = message.useMessage();
  const [deletingArray, setDeletingArray] = useState<string[]>([]);
  const params = {
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  };

  const { mutate: deleteApiKey } = useDeleteApiKey();

  const onDelete = (record: IApiKey) => {
    setDeletingArray([...deletingArray, record.key]);
    deleteApiKey(
      {
        ...params,
        serviceKey: serviceKey,
        apiKeyId: record.key,
      },
      {
        onSettled: () => setDeletingArray(deletingArray.filter((i) => i !== record.key)),
      }
    );
  };

  // Project columns
  const columns = [
    {
      title: "Name",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Key",
      dataIndex: "key",
      key: "key",
      width: "35%",
      ellipsis: true,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at: string) => {
        const date = new Date(created_at);
        const day = date.getDate();
        const month = MONTHS_MAP[date.getMonth() as keyof typeof MONTHS_MAP];

        const year = date.getUTCFullYear();
        return `${day} ${t(`${month}`)} ${year}`;
      },
    },
    {
      title: "Actions",
      key: "action",
      // render: (_: any, record: IApiKey) => {
      render: (_: any, record: IApiKey) => {
        return (
          <Space size="middle">
            <Tooltip title={t("project_single.api_key.copy_tooltip")}>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(record.key);
                  messageApi.open({
                    content: t("project_single.api_key.successful_copy"),
                    type: "success",
                    duration: 8,
                  });
                }}
                icon={<CopyOutlined />}
                type="link"
              ></Button>
            </Tooltip>
            <Popconfirm
              title={t("project_single.api_key.delete_tooltip")}
              description={t("project_single.api_key.delete_confirmation")}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              onConfirm={() => onDelete(record)}
              zIndex={1001}
            >
              {/* Use ref to directly reference the element */}
              <div>
                <Tooltip title={t("project_single.api_key.delete_tooltip")} zIndex={1000} ref={React.createRef()}>
                  <Button loading={deletingArray.includes(record.key)} icon={<DeleteOutlined />} type="link" danger />
                </Tooltip>
              </div>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // If there are api keys inside the project then return the table
  const renderApiKeysTable = (apiKeys: IApiKey[]) => (
    <Row>
      <Col xs={24}>
        <Button className="float-right mb-3" type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
          {t("project_single.api_key.new_key")}
        </Button>

        <Table size="small" bordered dataSource={apiKeys} columns={columns} pagination={apiKeys.length > PAGINATION_OPTIONS.defaultPageSize ? { ...PAGINATION_OPTIONS } : false} />
      </Col>
    </Row>
  );

  return (
    <>
      {contextHolder}
      <Row>
        <Col>
          <p>
            <strong>{title}</strong>
          </p>
        </Col>
      </Row>
      {apiKeys?.length ? (
        renderApiKeysTable(apiKeys)
      ) : (
        <>
          <Alert className="mb-3" icon={<WarningOutlined />} showIcon type="warning" message={t("project_single.api_key.no_key")} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
            {t("project_single.api_key.new_key")}
          </Button>
        </>
      )}
      <ApiKeyModal
        isModalOpen={isModalOpen}
        serviceKey={serviceKey}
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
};
